export interface Recipe {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  ingredients: string[];
  steps: string[];
  categories?: string[]; // optional, tied to categories
}

export interface RecipeSelections {
  mealType: string[];
  cuisineStyle: string[];
  mainIngredients: string[];
  vegetablesFruits: string[];
  grainsStaples: string[];
  dietaryPreferences: string[];
  cookingMethods: string[];
  additionalModifiers: string[];
}

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://localhost:32771/api';
const API_ENDPOINTS = {
  generate: `${API_BASE_URL}/recipe/generate`,
  getRecipe: (id: string) => `${API_BASE_URL}/recipe/${id}`,
  rating: `${API_BASE_URL}/recipe/rating`,
  popular: `${API_BASE_URL}/recipe/popular`,
};

// Export the rating endpoint for use in components
export const RATING_ENDPOINT = API_ENDPOINTS.rating;

// Commented out mock implementation - now using real API
/*
export const mockGenerateRecipes = (request: { selections: RecipeSelections }): Promise<Recipe[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      // Build prompt from selections for simulation
      const { selections } = request;
      const promptParts: string[] = [];
      if (selections.mealType.length) promptParts.push(`for ${selections.mealType.join(' or ')}`);
      if (selections.cuisineStyle.length) promptParts.push(`in ${selections.cuisineStyle.join(' or ')} style`);
      if (selections.mainIngredients.length || selections.vegetablesFruits.length) {
        const ingredients = [...selections.mainIngredients, ...selections.vegetablesFruits];
        promptParts.push(`using ${ingredients.join(', ')}`);
      }
      if (selections.grainsStaples.length) promptParts.push(`with ${selections.grainsStaples.join(', ')}`);
      if (selections.dietaryPreferences.length) promptParts.push(`that's ${selections.dietaryPreferences.join(', ')}`);
      if (selections.cookingMethods.length) promptParts.push(`prepared by ${selections.cookingMethods.join(', ')}`);
      if (selections.additionalModifiers.length) promptParts.push(`and is ${selections.additionalModifiers.join(', ')}`);
      
      // Simulate generating recipes based on prompt (using mock data)
      const numRecipes = Math.floor(Math.random() * 3) + 3; // 3-5 recipes
      const shuffled = [...mockRecipes].sort(() => 0.5 - Math.random());
      const results = shuffled.slice(0, numRecipes).map(recipe => ({
        ...recipe,
        categories: [
          ...selections.mealType,
          ...selections.cuisineStyle,
          ...selections.mainIngredients,
          ...selections.vegetablesFruits,
          ...selections.grainsStaples,
          ...selections.dietaryPreferences,
          ...selections.cookingMethods,
          ...selections.additionalModifiers,
        ].filter(Boolean),
      }));
      resolve(results);
    }, 1000);
  });
};
*/

// Real API implementation
export const mockGenerateRecipes = async (request: { 
  selections: RecipeSelections; 
  mode?: 'generate' | 'regenerate' | 'find' | 'popular';
  userId: string;
}): Promise<Recipe[]> => {
  try {
    const requestBody = {
      userId: request.userId, // Use passed userId instead of hardcoded
      selections: request.selections,
      mode: request.mode || 'generate',
    };

    const response = await fetch(API_ENDPOINTS.generate, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const recipes: Recipe[] = await response.json();
    return recipes;
  } catch (error) {
    console.error('Error calling recipe generation API:', error);
    throw error;
  }
};

export const mockGetRecipeById = async (id: string): Promise<Recipe> => {
  try {
    const response = await fetch(API_ENDPOINTS.getRecipe(id), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const recipe: Recipe = await response.json();
    return recipe;
  } catch (error) {
    console.error('Error calling recipe fetch API:', error);
    throw error;
  }
};

export const mockGetPopularRecipes = async (limit: number = 10, minRatings: number = 1): Promise<Recipe[]> => {
  try {
    const url = new URL(API_ENDPOINTS.popular);
    url.searchParams.append('limit', limit.toString());
    url.searchParams.append('minRatings', minRatings.toString());

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const recipes: Recipe[] = await response.json();
    return recipes;
  } catch (error) {
    console.error('Error calling popular recipes API:', error);
    throw error;
  }
};